import * as React from 'react'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {type IconProp} from '@fortawesome/fontawesome-svg-core'
import {asHTMLAttributeValue} from '@freckle/maybe'
import {cn} from '@frontrow/console/src/lib/utils'
import type {PolyfilledEvent} from '@frontrow/console/src/polyfills'

export type ButtonStyleT = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'link'

type ButtonTypeT = 'button' | 'submit'

export type Props = {
  /** Action of the button */
  onClick?: (e: PolyfilledEvent) => void
  /** Content of the button */
  children?: React.ReactNode
  /** Additional CSS Class to add to the button */
  addClass?: string | null
  /** Define the style of the Button */
  style?: ButtonStyleT
  disabled?: boolean
  /** Defines HTML button type attribute in `button`, `submit` */
  type?: ButtonTypeT
  dataTest?: string
  title?: string
  leftIcon?: IconProp
}

/**
 * This is the standard Button.
 *
 * It contains the most used attribute of the regular HTML `<button>` tag.
 *
 */
export function Button({
  style = 'primary',
  disabled = false,
  type = 'button',
  onClick,
  children,
  dataTest,
  title,
  leftIcon,
  addClass
}: Props): React.ReactElement {
  const handleClick = disabled === true ? preventClick : onClick

  const className = getClassName({style, addClass})

  const leftIconNode =
    leftIcon !== null && leftIcon !== undefined ? (
      <span className="pr-3">
        <FontAwesomeIcon icon={leftIcon} aria-hidden="true" />
      </span>
    ) : null

  return (
    <button
      onClick={handleClick}
      type={type}
      className={className}
      disabled={disabled}
      title={asHTMLAttributeValue(title)}
      data-test={asHTMLAttributeValue(dataTest)}
    >
      {leftIconNode}
      {children}
    </button>
  )
}

function preventClick(e: PolyfilledEvent) {
  e.preventDefault()
}

const BUTTON_BASE =
  'inline-block text-center rounded font-semibold border border-solid px-5 py-px cursor-pointer align-middle break-normal' +
  ' disabled:border-chrome disabled:bg-chrome disabled:text-white disabled:cursor-not-allowed' +
  // Preserve the SCSS `.button + .button { margin-left: $std-margin-xxs }` (4px) adjacent-button spacing.
  ' [button+&]:ml-1'

const STYLE_CLASSES: Record<Exclude<ButtonStyleT, 'link'>, string> = {
  primary:
    'bg-primary text-white border-primary' +
    ' hover:bg-primary-dark hover:text-white hover:border-primary-dark' +
    ' focus:bg-primary-dark focus:text-white focus:outline-none focus:shadow-focus-ring',
  secondary:
    'bg-white text-primary border-primary' +
    ' hover:bg-white hover:text-primary-dark hover:border-primary-dark' +
    ' focus:bg-white focus:text-primary-dark focus:outline-none focus:shadow-focus-ring',
  tertiary:
    'bg-white text-body border-chrome' +
    ' hover:bg-surface-hover hover:text-body hover:border-chrome' +
    ' focus:bg-surface-hover focus:outline-none focus:shadow-focus-ring',
  danger:
    'bg-destructive text-white border-destructive' +
    ' hover:bg-destructive-dark hover:text-white hover:border-destructive-dark' +
    ' focus:bg-destructive-dark focus:text-white focus:outline-none focus:shadow-focus-ring'
}

const LINK_CLASS =
  'inline-block text-primary border-0 p-0 bg-transparent text-[length:inherit] font-[inherit] cursor-pointer' +
  ' hover:text-primary-dark focus:shadow-focus-ring-sm focus:outline-none' +
  ' disabled:bg-transparent disabled:border-0 disabled:text-muted-text' +
  ' [button+&]:ml-1'

function getClassName({addClass, style = 'primary'}: Pick<Props, 'addClass' | 'style'>): string {
  if (style === 'link') {
    return cn(LINK_CLASS, addClass)
  }
  return cn(BUTTON_BASE, STYLE_CLASSES[style], addClass)
}
